import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  Flex,
  Heading,
  Switch,
  Text,
  useToast
} from '@chakra-ui/react'
import { FC, useCallback, useEffect, useState } from 'react'
import { useImmer } from 'use-immer'

import useFilters from '@hook/shared/use-filters'

import UsuarioSapSelect, {
  podeVincularUsuarioSap
} from '@component/Modules/Users/UsuarioSapSelect'
import Filter from '@component/Shared/Filter'

import user from '@service/user'

// Campos do vínculo SAP: ausentes no JSON da API = sem vínculo
const USUARIO_SAP_VAZIO = {
  usuarioSap: null as string | null,
  usuarioSapNome: null as string | null,
  usuarioSapAtivo: null as boolean | null
}

interface IUsersModalProps {
  data?: { [key: string]: any }
  isOpen: boolean
  onClose: () => void
  onUpdate: () => void
}

const UsersModal: FC<IUsersModalProps> = ({
  isOpen,
  onClose,
  data = undefined,
  onUpdate
}) => {
  const { profiles, getFilters, data: dataProps } = useFilters()

  const toast = useToast()

  const [submitting, setSubmitting] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    let active = true
    setLocalLoading(true)

    const loadFilters = async () => {
      try {
        await getFilters({
          profiles: true,
          companiesConfig: true,
          centersConfig: true,
          compactCategoriesConfig: true,
          productManagersConfig: true
        })
      } finally {
        if (active) setLocalLoading(false)
      }
    }
    loadFilters()

    return () => {
      active = false
    }
    // Busca só quando o modal abre: getFilters muda de identidade a cada
    // atualização de `data` e, com ele nas deps, um filtro que volta vazio
    // dispararia novas buscas em loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])
  const [form, updateForm] = useImmer({
    nome: '',
    login: '',
    email: '',
    idPerfil: 1,
    revisarStatus: false,
    acessoAgendamento: false,
    acessoRelatorioGeral: false,
    gerentesCategoria: [],
    empresas: [],
    gerentesProduto: [],
    lojas: [],
    mundos: [],
    categoriasCompactas: [],
    ...USUARIO_SAP_VAZIO
  })

  const handleInputChange = (e: any) => {
    const { name, value } = e.target

    updateForm((draft: any) => {
      draft[name] = value
    })
  }

  const handleSwitchChange = (e: any) => {
    const { name } = e.target

    updateForm((draft: any) => {
      draft[name] = !draft[name]
    })
  }

  const handleModalClose = () => {
    onClose()
    updateForm(draft => {
      return Object.assign(draft, {
        nome: '',
        login: '',
        email: '',
        idPerfil: 1,
        revisarStatus: false,
        acessoAgendamento: false,
        acessoRelatorioGeral: false,
        gerentesCategoria: [],
        empresas: [],
        gerentesProduto: [],
        lojas: [],
        mundos: [],
        categoriasCompactas: [],
        ...USUARIO_SAP_VAZIO
      })
    })
  }

  const exibirUsuarioSap = podeVincularUsuarioSap(form.idPerfil)

  const getErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || fallback

  const handleSave = () => {
    // usuarioSap vai sempre (inclusive null): o PUT substitui o cadastro inteiro
    // e perfis fora de 1/12/13 não podem ter vínculo
    const {
      usuarioSapNome: _usuarioSapNome,
      usuarioSapAtivo: _usuarioSapAtivo,
      ...rest
    } = form
    const formSave = {
      ...rest,
      usuarioSap: exibirUsuarioSap ? form.usuarioSap || null : null
    }
    // formSave.nome = form.nome.trim()
    if (!validateEmail(form.email)) {
      toast({
        title: 'Error',
        description: 'formato do email incorreto',
        status: 'warning',
        position: 'top',
        duration: 5000,
        isClosable: true
      })
      return
    }
    setSubmitting(true)
    if (data) {
      user
        .update(formSave, data?.email)
        .then(() => {
          toast({
            title: 'Sucesso',
            description: 'Usuário atualizado com sucesso!',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          handleModalClose()
          onUpdate()
        })
        .catch(error => {
          toast({
            title: 'Erro',
            description: getErrorMessage(error, 'Erro ao atualizar usuário!'),
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        })
        .finally(() => {
          setSubmitting(false)
        })
    } else {
      user
        .create(formSave)
        .then(() => {
          toast({
            title: 'Sucesso',
            description: 'Usuário criado com sucesso!',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          handleModalClose()
          onUpdate()
        })
        .catch(error => {
          toast({
            title: 'Erro',
            description: getErrorMessage(error, 'Erro ao criar usuário!'),
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  const handleUpdateForm = useCallback(() => {
    updateForm(draft => {
      return Object.assign(draft, USUARIO_SAP_VAZIO, data)
    })
  }, [updateForm, data])

  useEffect(() => {
    handleUpdateForm()
  }, [handleUpdateForm, data])

  const disabledSubmit =
    form.nome === '' || form.email === '' || form.login === ''
  const validateEmail = (email: string) => {
    // Use a regular expression for basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderRadius="5px 5px 0 0" bgColor="white">
          {data ? 'Editar usuário' : 'Novo usuário'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody bgColor="white">
          <Grid templateColumns="1.2fr 1fr" gap="24px" mb="24px">
            <FormControl isRequired>
              <FormLabel>Nome</FormLabel>
              <Input
                placeholder="Nome do usuário"
                name="nome"
                borderColor="blackAlpha.400"
                value={form.nome}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Perfil</FormLabel>
              <Select
                value={form.idPerfil}
                name="idPerfil"
                borderColor="blackAlpha.400"
                onChange={handleInputChange}
              >
                <option style={{ background: 'white' }} disabled>
                  Selecione
                </option>
                {profiles.map((profile: any) => (
                  <option
                    style={{ background: 'white' }}
                    key={`profile-option-${profile.value}`}
                    value={profile.value}
                  >
                    {profile.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid templateColumns="1.8fr 1fr" gap="24px" mb="24px">
            <FormControl isRequired>
              <FormLabel>E-mail</FormLabel>
              <Input
                placeholder="Informe o e-mail do usuário"
                name="email"
                borderColor="blackAlpha.400"
                value={form.email}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Login</FormLabel>
              <Input
                placeholder="Informe o login"
                name="login"
                borderColor="blackAlpha.400"
                value={form.login}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>

          {exibirUsuarioSap && (
            <Box mb="24px">
              <UsuarioSapSelect
                usuarioId={data?.id}
                value={
                  form.usuarioSap
                    ? {
                        value: form.usuarioSap,
                        label: form.usuarioSapNome
                          ? `${form.usuarioSap} - ${form.usuarioSapNome}`
                          : form.usuarioSap
                      }
                    : null
                }
                onChange={option => {
                  updateForm(draft => {
                    draft.usuarioSap = option?.value ?? null
                    draft.usuarioSapNome = option?.nome ?? null
                    // Opções da busca são sempre usuários SAP ativos
                    draft.usuarioSapAtivo = option ? true : null
                  })
                }}
              />
              {form.usuarioSap && form.usuarioSapAtivo === false && (
                <Text mt={1} fontSize="sm" color="orange.500">
                  Este usuário SAP não consta mais na base do SAP. Selecione
                  outro.
                </Text>
              )}
            </Box>
          )}

          <Grid templateColumns="repeat(1, 1fr)" gap="24px" mb="24px">
            <Filter
              label="Empresa"
              options={dataProps.companiesConfig}
              value={form.empresas.map(
                (empresa: any) =>
                  dataProps.companiesConfig.find(
                    company => company.value === empresa
                  )!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.empresas = values!.map((option: any) => option.value)
                })
              }}
              isLoading={localLoading}
              isMulti
            />
            <Filter
              label="Loja"
              options={dataProps.centersConfig}
              value={form.lojas.map(
                (loja: any) =>
                  dataProps.centersConfig.find(store => store.value === loja)!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.lojas = values!.map((option: any) => option.value)
                })
              }}
              isLoading={localLoading}
              isMulti
            />
            <Filter
              label="Categoria Compacta"
              options={dataProps.compactCategoriesConfig}
              value={form.categoriasCompactas.map(
                (mundo: any) =>
                  dataProps.compactCategoriesConfig.find(
                    compactCategoriesConfig =>
                      compactCategoriesConfig.value === mundo
                  )!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.categoriasCompactas = values!.map(
                    (option: any) => option.value
                  )
                })
              }}
              isLoading={localLoading}
              isMulti
            />
            {/* <Filter
              label="Mundo"
              options={dataProps.worldsConfig}
              value={form.mundos.map(
                (mundo: any) =>
                  dataProps.worldsConfig.find(world => world.value === mundo)!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.mundos = values!.map((option: any) => option.value)
                })
              }}
              isLoading={loading}
              isMulti
            /> */}
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap="24px" mb="24px">
            {/* <Filter
              label="Gerente de Categoria"
              options={dataProps.categoryManagersConfig}
              value={form.gerentesCategoria.map(
                (gerente: any) =>
                  dataProps.categoryManagersConfig.find(
                    (option: any) => option.value === gerente
                  )!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.gerentesCategoria = values!.map(
                    (option: any) => option.value
                  )
                })
              }}
              isLoading={loading}
              isMulti
            /> */}

            <Filter
              label="Gerente de Produto"
              options={dataProps.productManagersConfig}
              value={form.gerentesProduto.map(
                (gerente: any) =>
                  dataProps.productManagersConfig.find(
                    (option: any) => option.value === gerente
                  )!
              )}
              onChange={values => {
                updateForm((draft: any) => {
                  draft.gerentesProduto = values!.map(
                    (option: any) => option.value
                  )
                })
              }}
              isLoading={localLoading}
              isMulti
            />
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap="24px">
            <Flex
              p={4}
              bg="gray.100"
              alignItems="center"
              justifyContent="space-between"
              borderRadius={8}
            >
              <Heading size="sm">Acesso ao Agendamento</Heading>
              <Switch
                colorScheme="yellow"
                name="acessoAgendamento"
                isChecked={form.acessoAgendamento}
                onChange={handleSwitchChange}
              />
            </Flex>
            <Flex
              p={4}
              bg="gray.100"
              alignItems="center"
              justifyContent="space-between"
              borderRadius={8}
            >
              <Heading size="sm">Acesso ao RelatorioGeral</Heading>
              <Switch
                colorScheme="yellow"
                name="acessoRelatorioGeral"
                isChecked={form.acessoRelatorioGeral}
                onChange={handleSwitchChange}
              />
            </Flex>
            <Flex
              p={4}
              bg="gray.100"
              alignItems="center"
              justifyContent="space-between"
              borderRadius={8}
            >
              <Heading size="sm">Revisar status</Heading>
              <Switch
                colorScheme="yellow"
                name="revisarStatus"
                isChecked={form.revisarStatus}
                onChange={handleSwitchChange}
              />
            </Flex>
          </Grid>
        </ModalBody>
        <ModalFooter borderRadius="0 0 5px 5px" bgColor="white">
          <Button
            colorScheme="green"
            onClick={handleSave}
            isLoading={submitting}
            isDisabled={submitting || disabledSubmit}
          >
            {data ? 'Atualizar' : 'Adicionar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default UsersModal
